import React, { useMemo, useState } from "react";
import {
	Alert,
	Button,
	DatePicker,
	Empty,
	Flex,
	Popover,
	Select,
	Spin,
	Statistic,
	Table,
	Tabs,
	Typography,
	message,
} from "antd";
import {
	DownloadOutlined,
	DownOutlined,
	SettingOutlined,
	UpOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useGetExportAllocationDataByDateAndUserQuery } from "@/redux/services/allocationApi";
import { useGetDistributionsQuery } from "@/redux/services/distribution";
import { useGetTemplatesQuery } from "@/redux/services/phoneTemplateApi";
import { useGetVehiclesQuery } from "@/redux/services/vehicleApi";
import { useGetDriversQuery } from "@/redux/services/driverApi";
import { useGetPickupLocationQuery } from "@/redux/services/pickupApi";
import { useGetProductListQuery } from "@/redux/services/productApi";

const dateFormat = "YYYY-MM-DD";
const selectedColumnsStorageKey = "exportPage.selectedColumnKeys";
const selectedColumnsVersionKey = "exportPage.selectedColumnKeysVersion";
const selectedColumnsVersion = "3";
const selectedVehicleColumnsStorageKey = "exportPage.selectedVehicleColumnKeys";
const selectedVehicleColumnsVersionKey =
	"exportPage.selectedVehicleColumnKeysVersion";
const selectedVehicleColumnsVersion = "3";
const { RangePicker } = DatePicker;
const exportTabKeys = {
	order: "order",
	vehicle: "vehicle",
	settings: "settings",
};

const getTripDetailUrl = (orderSubId) =>
	`http://aktravel.com.au/trip-detail/${encodeURIComponent(orderSubId)}`;

const exportColumns = [
	{ title: "日期", dataIndex: "travel_date", key: "travel_date" },
	{
		title: "主键",
		dataIndex: "order_sub_id",
		key: "order_sub_id",
	},
	{
		title: "链接",
		dataIndex: "trip_detail_url",
		key: "trip_detail_url",
		render: (url) => {
			if (!url) {
				return "";
			}

			return (
				<a href={url} target="_blank" rel="noreferrer">
					{url}
				</a>
			);
		},
	},
	{ title: "产品 ID", dataIndex: "product_id", key: "product_id" },
	{ title: "产品", dataIndex: "product_title", key: "product_title" },
	{ title: "车牌", dataIndex: "vehicle_plate_number", key: "vehicle_plate_number" },
	{ title: "车型", dataIndex: "vehicle_model", key: "vehicle_model" },
	{ title: "总座位", dataIndex: "vehicle_total_seats", key: "vehicle_total_seats" },
	{ title: "司机", dataIndex: "driver_name", key: "driver_name" },
	{ title: "司机电话", dataIndex: "driver_phone", key: "driver_phone" },
	{ title: "渠道", dataIndex: "user_name", key: "user_name" },
	{ title: "渠道简称", dataIndex: "user_short_name", key: "user_short_name" },
	{ title: "订单号", dataIndex: "order_id", key: "order_id" },
	{ title: "选项", dataIndex: "option", key: "option" },
	{ title: "联系人", dataIndex: "client_name", key: "client_name" },
	{ title: "电话", dataIndex: "client_phone", key: "client_phone" },
	{ title: "上车地点", dataIndex: "pickup_location", key: "pickup_location" },
	{ title: "上车地址", dataIndex: "pickup_address", key: "pickup_address" },
	{ title: "上车时间", dataIndex: "pickup_time", key: "pickup_time" },
	{ title: "人数", dataIndex: "people_no", key: "people_no" },
	{ title: "备注", dataIndex: "comment", key: "comment" },
];

const vehicleExportColumns = [
	{ title: "日期", dataIndex: "travel_date", key: "travel_date" },
	{ title: "主键", dataIndex: "vehicle_key", key: "vehicle_key" },
	{ title: "产品 ID", dataIndex: "product_id", key: "product_id" },
	{ title: "产品", dataIndex: "product_title", key: "product_title" },
	{ title: "车牌", dataIndex: "vehicle_plate_number", key: "vehicle_plate_number" },
	{ title: "车型", dataIndex: "vehicle_model", key: "vehicle_model" },
	{ title: "座位", dataIndex: "vehicle_total_seats", key: "vehicle_total_seats" },
	{ title: "司机", dataIndex: "driver_name", key: "driver_name" },
	{ title: "司机电话", dataIndex: "driver_phone", key: "driver_phone" },
	{ title: "上车人数", dataIndex: "total_people_no", key: "total_people_no" },
];

const settingExportColumns = {
	templates: [
		{ title: "模板 ID", dataIndex: "id", key: "id" },
		{ title: "模板名称", dataIndex: "name", key: "name" },
		{ title: "模板内容", dataIndex: "template", key: "template" },
	],
	vehicles: [
		{ title: "车辆 ID", dataIndex: "vehicle_id", key: "vehicle_id" },
		{ title: "车牌", dataIndex: "vehicle_plate_number", key: "vehicle_plate_number" },
		{ title: "型号", dataIndex: "vehicle_model", key: "vehicle_model" },
		{ title: "颜色", dataIndex: "vehicle_color", key: "vehicle_color" },
		{ title: "总人数", dataIndex: "vehicle_total_seats", key: "vehicle_total_seats" },
		{ title: "可容纳人数", dataIndex: "vehicle_available_seats", key: "vehicle_available_seats" },
		{ title: "状态", dataIndex: "vehicle_status", key: "vehicle_status" },
	],
	drivers: [
		{ title: "司机 ID", dataIndex: "user_detail_id", key: "user_detail_id" },
		{ title: "Email", dataIndex: "user_email", key: "user_email" },
		{ title: "名字", dataIndex: "user_name", key: "user_name" },
		{ title: "电话", dataIndex: "user_phone", key: "user_phone" },
		{ title: "状态", dataIndex: "user_status", key: "user_status" },
	],
	pickupLocations: [
		{ title: "接送点 ID", dataIndex: "location_id", key: "location_id" },
		{ title: "名字", dataIndex: "location_name", key: "location_name" },
		{ title: "地址", dataIndex: "location_address", key: "location_address" },
	],
	products: [
		{ title: "产品 ID", dataIndex: "product_id", key: "product_id" },
		{ title: "名字", dataIndex: "product_title", key: "product_title" },
		{ title: "副标题", dataIndex: "product_subtitle", key: "product_subtitle" },
		{ title: "描述", dataIndex: "product_description", key: "product_description" },
		{ title: "颜色", dataIndex: "product_color", key: "product_color" },
	],
	distributions: [
		{ title: "渠道 ID", dataIndex: "user_detail_id", key: "user_detail_id" },
		{ title: "名字", dataIndex: "user_name", key: "user_name" },
		{ title: "简称", dataIndex: "user_short_name", key: "user_short_name" },
		{ title: "电话", dataIndex: "user_phone", key: "user_phone" },
		{ title: "主邮箱", dataIndex: "user_email", key: "user_email" },
		{ title: "其他邮箱", dataIndex: "user_msg_email", key: "user_msg_email" },
		{ title: "备忘", dataIndex: "user_note", key: "user_note" },
		{ title: "状态", dataIndex: "user_status", key: "user_status" },
		{ title: "发送消息", dataIndex: "is_send_message", key: "is_send_message" },
	],
};

const allColumnKeys = exportColumns.map((column) => column.key);
const allVehicleColumnKeys = vehicleExportColumns.map((column) => column.key);
const numericColumnKeys = new Set([
	"people_no",
	"total_people_no",
	"vehicle_total_seats",
	"vehicle_available_seats",
]);

const getInitialSelectedColumnKeys = () => {
	if (typeof window === "undefined") {
		return allColumnKeys;
	}

	try {
		const storedValue = window.sessionStorage.getItem(
			selectedColumnsStorageKey
		);
		const parsedValue = storedValue ? JSON.parse(storedValue) : null;

		if (!Array.isArray(parsedValue)) {
			return allColumnKeys;
		}

		const validColumnKeys = parsedValue.filter((key) =>
			allColumnKeys.includes(key)
		);

		if (validColumnKeys.length === 0) {
			return allColumnKeys;
		}

		if (
			window.sessionStorage.getItem(selectedColumnsVersionKey) !==
				selectedColumnsVersion &&
			(!validColumnKeys.includes("order_sub_id") ||
				!validColumnKeys.includes("trip_detail_url"))
		) {
			const nextColumnKeys = [
				"travel_date",
				"order_sub_id",
				"trip_detail_url",
				...validColumnKeys.filter((key) => key !== "travel_date"),
			].filter((key, index, keys) => keys.indexOf(key) === index);
			saveSelectedColumnKeys(nextColumnKeys);
			return nextColumnKeys;
		}

		return validColumnKeys;
	} catch (error) {
		return allColumnKeys;
	}
};

const getInitialSelectedVehicleColumnKeys = () => {
	if (typeof window === "undefined") {
		return allVehicleColumnKeys;
	}

	try {
		const storedValue = window.sessionStorage.getItem(
			selectedVehicleColumnsStorageKey
		);
		const parsedValue = storedValue ? JSON.parse(storedValue) : null;

		if (!Array.isArray(parsedValue)) {
			return allVehicleColumnKeys;
		}

		const validColumnKeys = parsedValue.filter((key) =>
			allVehicleColumnKeys.includes(key)
		);

		if (
			validColumnKeys.length === 0 ||
			window.sessionStorage.getItem(selectedVehicleColumnsVersionKey) !==
				selectedVehicleColumnsVersion
		) {
			saveSelectedVehicleColumnKeys(allVehicleColumnKeys);
			return allVehicleColumnKeys;
		}

		return validColumnKeys;
	} catch (error) {
		return allVehicleColumnKeys;
	}
};

const saveSelectedColumnKeys = (columnKeys) => {
	if (typeof window === "undefined") {
		return;
	}

	try {
		window.sessionStorage.setItem(
			selectedColumnsStorageKey,
			JSON.stringify(columnKeys)
		);
		window.sessionStorage.setItem(
			selectedColumnsVersionKey,
			selectedColumnsVersion
		);
	} catch (error) {
		// Ignore storage errors so field selection still works in-memory.
	}
};

const saveSelectedVehicleColumnKeys = (columnKeys) => {
	if (typeof window === "undefined") {
		return;
	}

	try {
		window.sessionStorage.setItem(
			selectedVehicleColumnsStorageKey,
			JSON.stringify(columnKeys)
		);
		window.sessionStorage.setItem(
			selectedVehicleColumnsVersionKey,
			selectedVehicleColumnsVersion
		);
	} catch (error) {
		// Ignore storage errors so field selection still works in-memory.
	}
};

const normalizeValue = (value) => {
	if (value === null || value === undefined) {
		return "";
	}
	if (Array.isArray(value)) {
		return value.join(";");
	}
	if (typeof value === "object") {
		return JSON.stringify(value);
	}
	return value;
};

const escapeXml = (value) =>
	String(normalizeValue(value))
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");

const getDistributionValue = (distribution) =>
	String(
		distribution.user_detail_id ??
			distribution.id ??
			distribution.user_id ??
			distribution.user_name
	);

const getDistributionLabel = (distribution) => {
	if (!distribution) {
		return "全部渠道";
	}

	return distribution.user_short_name || distribution.user_name;
};

const sanitizeFileName = (value) =>
	String(value).replace(/[\\/:*?"<>|]/g, "_");

const getDateRangeLabel = (startDate, endDate) =>
	startDate === endDate ? startDate : `${startDate}_${endDate}`;

const formatTravelDate = (value, fallbackDate) => {
	if (!value) {
		return fallbackDate;
	}

	const parsedDate = dayjs(value);

	if (!parsedDate.isValid()) {
		return value;
	}

	return parsedDate.format(dateFormat);
};

const formatVehicleKeyDate = (value) => dayjs(value).format("YYYYMMDD");

const normalizeRows = (data = [], startDate) =>
	data.map((item, index) => ({
		key: item.order_sub_id ?? `${item.order_id ?? "order"}-${index}`,
		...item,
		trip_detail_url: item.order_sub_id
			? getTripDetailUrl(item.order_sub_id)
			: "",
		travel_date: formatTravelDate(item.travel_date, startDate),
	}));

const normalizeVehicleRows = (rows = []) => {
	const groupedVehicles = new Map();

	rows.forEach((row, index) => {
		const travelDate = row.travel_date;
		const productId = row.product_id || `未知产品-${index}`;
		const vehiclePlateNumber =
			row.vehicle_plate_number || `未分配车辆-${index}`;
		const vehicleKey = `${travelDate}_${productId}_${vehiclePlateNumber}`;
		const peopleNo = Number(row.people_no || 0);

		if (!groupedVehicles.has(vehicleKey)) {
			groupedVehicles.set(vehicleKey, {
				key: vehicleKey,
				vehicle_key: `${formatVehicleKeyDate(travelDate)}_${row.vehicle_plate_number || ""}`,
				travel_date: travelDate,
				product_id: row.product_id || "",
				product_title: row.product_title || "",
				vehicle_plate_number: row.vehicle_plate_number || "",
				vehicle_model: row.vehicle_model || "",
				vehicle_total_seats: row.vehicle_total_seats || "",
				driver_name: row.driver_name || "",
				driver_phone: row.driver_phone || "",
				total_people_no: 0,
			});
		}

		const vehicle = groupedVehicles.get(vehicleKey);

		vehicle.total_people_no += Number.isFinite(peopleNo) ? peopleNo : 0;
	});

	return Array.from(groupedVehicles.values());
};

const moveItem = (items, fromIndex, toIndex) => {
	if (
		toIndex < 0 ||
		toIndex >= items.length ||
		fromIndex < 0 ||
		fromIndex >= items.length
	) {
		return items;
	}

	const nextItems = [...items];
	const [movedItem] = nextItems.splice(fromIndex, 1);
	nextItems.splice(toIndex, 0, movedItem);

	return nextItems;
};

const getExcelColumnName = (index) => {
	let columnName = "";
	let columnNumber = index + 1;

	while (columnNumber > 0) {
		const remainder = (columnNumber - 1) % 26;
		columnName = String.fromCharCode(65 + remainder) + columnName;
		columnNumber = Math.floor((columnNumber - 1) / 26);
	}

	return columnName;
};

const isValidNumberCell = (value) => {
	if (value === null || value === undefined || value === "") {
		return false;
	}

	return Number.isFinite(Number(value));
};

const createCellXml = (value, rowIndex, columnIndex, column) => {
	const cellReference = `${getExcelColumnName(columnIndex)}${rowIndex}`;

	if (column && numericColumnKeys.has(column.key) && isValidNumberCell(value)) {
		return `<c r="${cellReference}"><v>${Number(value)}</v></c>`;
	}

	return `<c r="${cellReference}" t="inlineStr"><is><t>${escapeXml(
		value
	)}</t></is></c>`;
};

const createWorksheetXml = (rows, selectedColumns) => {
	const columnCount = selectedColumns.length;
	const rowCount = rows.length + 1;
	const dimensionEnd = `${getExcelColumnName(columnCount - 1)}${rowCount}`;
	const columnWidths = selectedColumns
		.map(
			(_, index) =>
				`<col min="${index + 1}" max="${index + 1}" width="20" customWidth="1"/>`
		)
		.join("");
	const headerRow = `<row r="1">${selectedColumns
		.map((column, columnIndex) =>
			createCellXml(column.title, 1, columnIndex, null)
		)
		.join("")}</row>`;
	const dataRows = rows
		.map(
			(row, rowIndex) =>
				`<row r="${rowIndex + 2}">${selectedColumns
					.map((column, columnIndex) =>
						createCellXml(
							row[column.dataIndex],
							rowIndex + 2,
							columnIndex,
							column
						)
					)
					.join("")}</row>`
		)
		.join("");

	return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
	<dimension ref="A1:${dimensionEnd}"/>
	<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
	<cols>${columnWidths}</cols>
	<sheetData>${headerRow}${dataRows}</sheetData>
</worksheet>`;
};

const createCrc32Table = () => {
	const table = [];

	for (let index = 0; index < 256; index += 1) {
		let value = index;

		for (let bit = 0; bit < 8; bit += 1) {
			value =
				value & 1
					? 0xedb88320 ^ (value >>> 1)
					: value >>> 1;
		}

		table[index] = value >>> 0;
	}

	return table;
};

const crc32Table = createCrc32Table();

const getCrc32 = (bytes) => {
	let crc = 0xffffffff;

	bytes.forEach((byte) => {
		crc = crc32Table[(crc ^ byte) & 0xff] ^ (crc >>> 8);
	});

	return (crc ^ 0xffffffff) >>> 0;
};

const uint16 = (value) => {
	const bytes = new Uint8Array(2);
	const view = new DataView(bytes.buffer);
	view.setUint16(0, value, true);
	return bytes;
};

const uint32 = (value) => {
	const bytes = new Uint8Array(4);
	const view = new DataView(bytes.buffer);
	view.setUint32(0, value, true);
	return bytes;
};

const stringToBytes = (value) => new TextEncoder().encode(value);

const concatBytes = (parts) => {
	const totalLength = parts.reduce((total, part) => total + part.length, 0);
	const result = new Uint8Array(totalLength);
	let offset = 0;

	parts.forEach((part) => {
		result.set(part, offset);
		offset += part.length;
	});

	return result;
};

const createZip = (files) => {
	const localFileParts = [];
	const centralDirectoryParts = [];
	let offset = 0;

	files.forEach((file) => {
		const nameBytes = stringToBytes(file.name);
		const contentBytes = stringToBytes(file.content);
		const crc = getCrc32(contentBytes);
		const localHeaderOffset = offset;
		const localHeader = concatBytes([
			uint32(0x04034b50),
			uint16(20),
			uint16(0x0800),
			uint16(0),
			uint16(0),
			uint16(0),
			uint32(crc),
			uint32(contentBytes.length),
			uint32(contentBytes.length),
			uint16(nameBytes.length),
			uint16(0),
			nameBytes,
		]);
		const localFile = concatBytes([localHeader, contentBytes]);

		localFileParts.push(localFile);
		offset += localFile.length;

		centralDirectoryParts.push(
			concatBytes([
				uint32(0x02014b50),
				uint16(20),
				uint16(20),
				uint16(0x0800),
				uint16(0),
				uint16(0),
				uint16(0),
				uint32(crc),
				uint32(contentBytes.length),
				uint32(contentBytes.length),
				uint16(nameBytes.length),
				uint16(0),
				uint16(0),
				uint16(0),
				uint16(0),
				uint32(0),
				uint32(localHeaderOffset),
				nameBytes,
			])
		);
	});

	const centralDirectory = concatBytes(centralDirectoryParts);
	const endOfCentralDirectory = concatBytes([
		uint32(0x06054b50),
		uint16(0),
		uint16(0),
		uint16(files.length),
		uint16(files.length),
		uint32(centralDirectory.length),
		uint32(offset),
		uint16(0),
	]);

	return concatBytes([
		...localFileParts,
		centralDirectory,
		endOfCentralDirectory,
	]);
};

const createXlsxBlob = (rows, selectedColumns) => {
	const files = [
		{
			name: "[Content_Types].xml",
			content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
	<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
	<Default Extension="xml" ContentType="application/xml"/>
	<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
	<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
	<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`,
		},
		{
			name: "_rels/.rels",
			content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
	<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
		},
		{
			name: "xl/workbook.xml",
			content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
	<sheets><sheet name="导出" sheetId="1" r:id="rId1"/></sheets>
</workbook>`,
		},
		{
			name: "xl/_rels/workbook.xml.rels",
			content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
	<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
	<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
		},
		{
			name: "xl/styles.xml",
			content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
	<fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts>
	<fills count="1"><fill><patternFill patternType="none"/></fill></fills>
	<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
	<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
	<cellXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/></cellXfs>
</styleSheet>`,
		},
		{
			name: "xl/worksheets/sheet1.xml",
			content: createWorksheetXml(rows, selectedColumns),
		},
	];

	const zipBytes = createZip(files);

	return new Blob([zipBytes], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
};

const downloadXlsxFile = (rows, selectedColumns, fileName) => {
	const blob = createXlsxBlob(rows, selectedColumns);
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = `${sanitizeFileName(fileName)}.xlsx`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

const downloadExcelFile = (
	rows,
	selectedColumns,
	dateRangeLabel,
	selectedChannel,
	fileNamePrefix = "订单导出"
) => {
	const channelName =
		selectedChannel === null
			? "全部渠道"
			: getDistributionLabel(selectedChannel);
	downloadXlsxFile(
		rows,
		selectedColumns,
		`${fileNamePrefix}_${dateRangeLabel}_${channelName}`
	);
};

const normalizeSettingRows = (data = [], settingKey) =>
	(Array.isArray(data) ? data : []).map((item, index) => ({
		key:
			item.id ??
			item.user_detail_id ??
			item.vehicle_id ??
			item.location_id ??
			item.product_id ??
			`${settingKey}-${index}`,
		...item,
	}));

const ExportPage = () => {
	const [selectedDateRange, setSelectedDateRange] = useState(
		[
			dayjs().format(dateFormat),
			dayjs().format(dateFormat),
		]
	);
	const [selectedUserId, setSelectedUserId] = useState();
	const [selectedColumnKeys, setSelectedColumnKeys] = useState(
		getInitialSelectedColumnKeys
	);
	const [selectedVehicleColumnKeys, setSelectedVehicleColumnKeys] = useState(
		getInitialSelectedVehicleColumnKeys
	);
	const [activeExportTabKey, setActiveExportTabKey] = useState(
		exportTabKeys.order
	);
	const isVehicleTabActive = activeExportTabKey === exportTabKeys.vehicle;
	const isSettingsTabActive = activeExportTabKey === exportTabKeys.settings;

	const {
		data: exportData,
		isLoading: orderLoading,
		isFetching: orderFetching,
		isError: orderError,
	} = useGetExportAllocationDataByDateAndUserQuery(
		{
			startDate: selectedDateRange[0],
			endDate: selectedDateRange[1],
			userId: isVehicleTabActive ? undefined : selectedUserId,
		},
		{
			skip:
				isSettingsTabActive ||
				!selectedDateRange[0] ||
				!selectedDateRange[1],
		}
	);
	const {
		data: distributionList,
		isLoading: distributionLoading,
		isError: distributionError,
	} = useGetDistributionsQuery();
	const { data: templateSettings, isLoading: templateSettingsLoading } =
		useGetTemplatesQuery();
	const { data: vehicleSettings, isLoading: vehicleSettingsLoading } =
		useGetVehiclesQuery();
	const { data: driverSettings, isLoading: driverSettingsLoading } =
		useGetDriversQuery();
	const { data: pickupSettings, isLoading: pickupSettingsLoading } =
		useGetPickupLocationQuery();
	const { data: productSettings, isLoading: productSettingsLoading } =
		useGetProductListQuery();

	const distributions = useMemo(
		() => distributionList ?? [],
		[distributionList]
	);
	const selectedChannel = useMemo(
		() =>
			distributions.find(
				(distribution) =>
					getDistributionValue(distribution) === selectedUserId
			) ?? null,
		[distributions, selectedUserId]
	);

	const rows = useMemo(
		() => normalizeRows(exportData, selectedDateRange[0]),
		[exportData, selectedDateRange]
	);
	const vehicleRows = useMemo(() => normalizeVehicleRows(rows), [rows]);
	const dateRangeLabel = useMemo(
		() => getDateRangeLabel(selectedDateRange[0], selectedDateRange[1]),
		[selectedDateRange]
	);
	const selectedColumns = useMemo(
		() => {
			const columnMap = new Map(
				exportColumns.map((column) => [column.key, column])
			);

			return selectedColumnKeys
				.map((key) => columnMap.get(key))
				.filter(Boolean);
		},
		[selectedColumnKeys]
	);
	const selectedVehicleColumns = useMemo(
		() => {
			const columnMap = new Map(
				vehicleExportColumns.map((column) => [column.key, column])
			);

			return selectedVehicleColumnKeys
				.map((key) => columnMap.get(key))
				.filter(Boolean);
		},
		[selectedVehicleColumnKeys]
	);
	const totalPeople = useMemo(
		() =>
			rows.reduce(
				(total, row) => total + Number(row.people_no || 0),
				0
			),
		[rows]
	);
	const vehicleTotalPeople = useMemo(
		() =>
			vehicleRows.reduce(
				(total, row) => total + Number(row.total_people_no || 0),
				0
			),
		[vehicleRows]
	);
	const isExportDataLoading = orderLoading || orderFetching;
	const displayRows = isExportDataLoading ? [] : rows;
	const displayVehicleRows = isExportDataLoading ? [] : vehicleRows;
	const displayTotalPeople = isExportDataLoading ? 0 : totalPeople;
	const displayVehicleTotalPeople = isExportDataLoading
		? 0
		: vehicleTotalPeople;
	const settingExports = useMemo(
		() => [
			{
				key: "templates",
				label: "短信模版",
				data: templateSettings,
				loading: templateSettingsLoading,
				columns: settingExportColumns.templates,
			},
			{
				key: "vehicles",
				label: "车辆",
				data: vehicleSettings,
				loading: vehicleSettingsLoading,
				columns: settingExportColumns.vehicles,
			},
			{
				key: "drivers",
				label: "司机",
				data: driverSettings,
				loading: driverSettingsLoading,
				columns: settingExportColumns.drivers,
			},
			{
				key: "pickupLocations",
				label: "接送点",
				data: pickupSettings,
				loading: pickupSettingsLoading,
				columns: settingExportColumns.pickupLocations,
			},
			{
				key: "products",
				label: "产品",
				data: productSettings,
				loading: productSettingsLoading,
				columns: settingExportColumns.products,
			},
			{
				key: "distributions",
				label: "渠道",
				data: distributions,
				loading: distributionLoading,
				columns: settingExportColumns.distributions,
			},
		],
		[
			templateSettings,
			templateSettingsLoading,
			vehicleSettings,
			vehicleSettingsLoading,
			driverSettings,
			driverSettingsLoading,
			pickupSettings,
			pickupSettingsLoading,
			productSettings,
			productSettingsLoading,
			distributions,
			distributionLoading,
		]
	);
	const activeSelectedColumns = isVehicleTabActive
		? selectedVehicleColumns
		: selectedColumns;
	const activeSelectedColumnKeys = isVehicleTabActive
		? selectedVehicleColumnKeys
		: selectedColumnKeys;
	const activeAllColumnKeys = isVehicleTabActive
		? allVehicleColumnKeys
		: allColumnKeys;
	const activeColumnOptions = isVehicleTabActive
		? vehicleExportColumns
		: exportColumns;

	const handleExport = () => {
		const exportRows = isVehicleTabActive ? vehicleRows : rows;
		const exportColumnsToUse = activeSelectedColumns;
		const fileNamePrefix = isVehicleTabActive ? "车辆导出" : "订单导出";

		if (exportRows.length === 0) {
			message.warning("当前筛选条件下没有可导出的数据");
			return;
		}

		if (exportColumnsToUse.length === 0) {
			message.warning("请至少选择一个导出字段");
			return;
		}

		downloadExcelFile(
			exportRows,
			exportColumnsToUse,
			dateRangeLabel,
			isVehicleTabActive ? null : selectedChannel,
			fileNamePrefix
		);
	};

	const handleSettingExport = (setting) => {
		const settingRows = normalizeSettingRows(setting.data, setting.key);
		const settingColumns = setting.columns ?? [];

		if (settingRows.length === 0) {
			message.warning(`${setting.label}暂无可导出的数据`);
			return;
		}

		if (settingColumns.length === 0) {
			message.warning(`${setting.label}暂无可导出的字段`);
			return;
		}

		downloadXlsxFile(
			settingRows,
			settingColumns,
			`设置导出_${setting.label}_${dayjs().format(dateFormat)}`
		);
	};

	const updateSelectedColumns = (values, currentKeys, allKeys, setKeys, saveKeys) => {
		const selectedValueSet = new Set(values);
		const keptKeys = currentKeys.filter((key) =>
			selectedValueSet.has(key)
		);
		const addedKeys = allKeys.filter(
			(key) =>
				selectedValueSet.has(key) && !currentKeys.includes(key)
		);
		const nextColumnKeys = [...keptKeys, ...addedKeys];

		setKeys(nextColumnKeys);
		saveKeys(nextColumnKeys);
	};

	const handleSelectedColumnChange = (values) => {
		if (isVehicleTabActive) {
			updateSelectedColumns(
				values,
				selectedVehicleColumnKeys,
				allVehicleColumnKeys,
				setSelectedVehicleColumnKeys,
				saveSelectedVehicleColumnKeys
			);
			return;
		}

		updateSelectedColumns(
			values,
			selectedColumnKeys,
			allColumnKeys,
			setSelectedColumnKeys,
			saveSelectedColumnKeys
		);
	};

	const handleSelectAllColumns = () => {
		handleSelectedColumnChange(activeAllColumnKeys);
	};

	const handleMoveColumn = (fromIndex, toIndex) => {
		const nextColumnKeys = moveItem(
			activeSelectedColumnKeys,
			fromIndex,
			toIndex
		);

		if (isVehicleTabActive) {
			setSelectedVehicleColumnKeys(nextColumnKeys);
			saveSelectedVehicleColumnKeys(nextColumnKeys);
			return;
		}

		setSelectedColumnKeys(nextColumnKeys);
		saveSelectedColumnKeys(nextColumnKeys);
	};

	if (distributionLoading) {
		return <Spin tip="Loading..." size="large" />;
	}

	if (distributionError || (!isSettingsTabActive && orderError)) {
		return (
			<Alert
				message="导出数据加载失败"
				description="请刷新页面后重试"
				type="error"
				showIcon
			/>
		);
	}

	const columnOrderContent = (
		<Flex
			vertical
			gap={8}
			style={{
				width: 320,
				maxHeight: 360,
				overflowY: "auto",
			}}
		>
			<Typography.Text strong>字段顺序</Typography.Text>
			{activeSelectedColumns.length === 0 ? (
				<Typography.Text type="secondary">
					请先选择需要导出的字段
				</Typography.Text>
			) : (
				activeSelectedColumns.map((column, index) => (
					<Flex
						key={column.key}
						align="center"
						justify="space-between"
						style={{
							border: "1px solid #f0f0f0",
							borderRadius: 6,
							padding: "6px 8px",
						}}
					>
						<Typography.Text>{column.title}</Typography.Text>
						<Flex gap={4}>
							<Button
								size="small"
								icon={<UpOutlined />}
								disabled={index === 0}
								title="上移"
								onClick={() =>
									handleMoveColumn(index, index - 1)
								}
							/>
							<Button
								size="small"
								icon={<DownOutlined />}
								disabled={
									index === activeSelectedColumns.length - 1
								}
								title="下移"
								onClick={() =>
									handleMoveColumn(index, index + 1)
								}
							/>
						</Flex>
					</Flex>
				))
			)}
		</Flex>
	);
	const orderTabContent =
		selectedColumns.length === 0 ? (
			<Alert
				message="请至少选择一个导出字段"
				type="warning"
				showIcon
			/>
		) : displayRows.length === 0 && !isExportDataLoading ? (
			<Empty description="当前筛选条件下暂无数据" />
		) : (
			<Table
				rowKey="key"
				dataSource={displayRows}
				columns={selectedColumns}
				loading={isExportDataLoading}
				scroll={{
					x: Math.max(selectedColumns.length * 120, 800),
				}}
				pagination={{ pageSize: 20 }}
			/>
		);
	const vehicleTabContent =
		selectedVehicleColumns.length === 0 ? (
			<Alert
				message="请至少选择一个导出字段"
				type="warning"
				showIcon
			/>
		) : displayVehicleRows.length === 0 && !isExportDataLoading ? (
			<Empty description="当前筛选条件下暂无车辆数据" />
		) : (
			<Table
				rowKey="key"
				dataSource={displayVehicleRows}
				columns={selectedVehicleColumns}
				loading={isExportDataLoading}
				scroll={{
					x: Math.max(selectedVehicleColumns.length * 140, 800),
				}}
				pagination={{ pageSize: 20 }}
			/>
		);
	const settingsTabContent = (
		<Flex vertical gap="middle">
			<Typography.Text type="secondary">
				导出设置页中每个配置表的全部字段。
			</Typography.Text>
			<Flex gap="middle" wrap="wrap">
				{settingExports.map((setting) => {
					const rowCount = Array.isArray(setting.data)
						? setting.data.length
						: 0;
					const columnCount = setting.columns?.length ?? 0;

					return (
						<Flex
							key={setting.key}
							vertical
							gap={8}
							style={{
								width: 220,
								border: "1px solid #f0f0f0",
								borderRadius: 8,
								padding: 12,
							}}
						>
							<Typography.Text strong>
								{setting.label}
							</Typography.Text>
							<Typography.Text type="secondary">
								{setting.loading
									? "Loading..."
									: `${rowCount} 条记录 / ${columnCount} 个字段`}
							</Typography.Text>
							<Button
								icon={<DownloadOutlined />}
								loading={setting.loading}
								onClick={() => handleSettingExport(setting)}
							>
								导出
							</Button>
						</Flex>
					);
				})}
			</Flex>
		</Flex>
	);

	return (
		<Flex vertical gap="large">
			<Tabs
				activeKey={activeExportTabKey}
				onChange={setActiveExportTabKey}
				items={[
					{
						key: exportTabKeys.order,
						label: "订单明细",
					},
					{
						key: exportTabKeys.vehicle,
						label: "车辆明细",
					},
					{
						key: exportTabKeys.settings,
						label: "设置明细",
					},
				]}
			/>

			{!isSettingsTabActive && (
				<Flex gap="middle" align="center" wrap="wrap">
					<RangePicker
						style={{ width: 280 }}
						value={[
							dayjs(selectedDateRange[0], dateFormat),
							dayjs(selectedDateRange[1], dateFormat),
						]}
						format={dateFormat}
						allowClear={false}
						onChange={(date, dateString) => {
							setSelectedDateRange(dateString);
						}}
					/>
					{!isVehicleTabActive && (
						<Select
							allowClear
							showSearch
							style={{ minWidth: 280, flex: 1 }}
							placeholder="选择渠道，不选则导出全部渠道"
							optionFilterProp="label"
							value={selectedUserId}
							onChange={setSelectedUserId}
							options={distributions.map((distribution) => ({
								value: getDistributionValue(distribution),
								label: distribution.user_short_name
									? `${distribution.user_name} (${distribution.user_short_name})`
									: distribution.user_name,
							}))}
						/>
					)}
					<Button
						type="primary"
						icon={<DownloadOutlined />}
						onClick={handleExport}
						loading={isExportDataLoading}
					>
						生成 Excel
					</Button>
				</Flex>
			)}

			{!isSettingsTabActive && (
				<Flex gap="middle" align="center" wrap="wrap">
					<Typography.Text strong style={{ whiteSpace: "nowrap" }}>
						导出字段
					</Typography.Text>
					<Select
						mode="multiple"
						allowClear
						maxTagCount="responsive"
						style={{ minWidth: 360, flex: 1 }}
						placeholder="选择需要导出的字段"
						optionFilterProp="label"
						value={activeSelectedColumnKeys}
						onChange={handleSelectedColumnChange}
						options={activeColumnOptions.map((column) => ({
							value: column.key,
							label: column.title,
						}))}
					/>
					<Button onClick={handleSelectAllColumns}>全选</Button>
					<Popover
						trigger="click"
						placement="bottomRight"
						content={columnOrderContent}
					>
						<Button icon={<SettingOutlined />}>字段顺序</Button>
					</Popover>
				</Flex>
			)}

			{!isSettingsTabActive && (
				<Flex gap="large" wrap="wrap">
					<Statistic
						title={isVehicleTabActive ? "车辆数" : "订单明细"}
						value={
							isVehicleTabActive
								? displayVehicleRows.length
								: displayRows.length
						}
					/>
					<Statistic
						title="总人数"
						value={
							isVehicleTabActive
								? displayVehicleTotalPeople
								: displayTotalPeople
						}
					/>
					{!isVehicleTabActive && (
						<Statistic
							title="渠道"
							value={getDistributionLabel(selectedChannel)}
						/>
					)}
				</Flex>
			)}

			{isSettingsTabActive ? (
				settingsTabContent
			) : orderLoading ? (
				<Spin tip="Loading..." size="large" />
			) : isVehicleTabActive ? (
				vehicleTabContent
			) : (
				orderTabContent
			)}
		</Flex>
	);
};

export default ExportPage;
